// modules
import { AdminData, useViewEditUser } from "containers/ViewEditUser/useViewEditUser";
import { useRoleManagement } from "containers/RoleManagement/useRoleManagement";

// components
import PageTitle from "components/molecules/PageTitle/PageTitle";
import NewUserDetails from "components/organisms/NewUser/NewUserDetails";
import UserRolesGroup from "components/organisms/NewUser/UserRolesGroup";
import { FieldErrors, RoleGroupItem } from "containers/NewUser/useNewUser";
import RoleGroup from "components/molecules/RoleGroup/RoleGroup";
import { Button } from "antd";
import getIcon from "utils/getIcon";
import ButtonComponent from "components/atoms/Button/Button";
import { Head } from "components/molecules/Head/Head";
import Spinner from "components/atoms/Spinner/Spinner";

// Styles
import styles from "containers/ViewEditUser/viewEditUserPage.module.css";

// Constants
import { PageTitles, ViewEditUser as Constants, ViewMode } from "constants/global";

// Types
import { GroupDetails } from "services/api/types";
import { GroupPermissionItem, PermissionGroup } from "containers/NewGroup/useNewGroup";

type ComponentProps = {
	isUserActive: boolean;
	adminData: AdminData;
	is2FAChecked: boolean;
	mode: string;
	permissionList: PermissionGroup[];
	groupsList?: Array<GroupDetails>;
	isDetailsUpdated?: boolean;
	selectedRoles: Array<string>;
	selectedGroups: Array<string>;
	currentSelectedGroup: RoleGroupItem;
	isCreateUserButtonEnabled?: boolean;
	initialValues?: {
		first_name: string;
		last_name: string;
		username: string;
		email: string;
		mfa_enabled: boolean;
		groups: string[];
	};
	fieldErrors: FieldErrors;
	loading: boolean;
	backgroundLoading: boolean;
	hasMultipleLimitRoles: boolean;
	disable2FACheckbox?: string;
	validateFields: (arg: boolean) => void;
	handleMode: (value: string) => void;
	handleGoBack: () => void;
	handleAdminDataChange: (value: { id: string; value: string }) => void;
	handleEnable2FA: (value: boolean) => void;
	setCurrentSelectedGroup: (value: string, id: string) => void;
	handleRemoveSelectedGroup: (value: string) => void;
	handleAddAnotherRole: () => void;
	handleDeactivateUser: () => void;
	handleLockUser: () => void;
	handleResendPassword: () => void;
	canResendPassword: boolean;
	submitChanges: () => void;
	activateUser: () => void;
	navigateToRoleManagement: () => void;
};

const ViewEditUserPage = () => {
	const componentProps: ComponentProps = useViewEditUser();
	const { createUserAccess } = useRoleManagement();

	const {
		isUserActive,
		adminData,
		is2FAChecked,
		mode,
		groupsList,
		permissionList,
		selectedRoles,
		selectedGroups,
		currentSelectedGroup,
		isDetailsUpdated,
		initialValues,
		fieldErrors,
		loading,
		backgroundLoading,
		hasMultipleLimitRoles,
		disable2FACheckbox,
		activateUser,
		validateFields,
		setCurrentSelectedGroup,
		handleRemoveSelectedGroup,
		handleMode,
		handleAddAnotherRole,
		handleAdminDataChange,
		handleEnable2FA,
		handleGoBack,
		handleDeactivateUser,
		handleLockUser,
		handleResendPassword,
		canResendPassword,
		submitChanges,
		navigateToRoleManagement
	} = componentProps;

	if (loading) {
		return <Spinner />;
	}

	return (
		<div className={styles?.root}>
			{backgroundLoading && <Spinner type="transparent" />}
			<Head>{PageTitles.EDIT_USER}</Head>
			<PageTitle showBackIcon={true} title={Constants.PAGE_TITLE} subTitle={Constants.PAGE_SUBTITLE} goBack={navigateToRoleManagement} />
			<div className={styles?.main_wrapper}>
				<div className={styles?.content}>
					<NewUserDetails
						readOnly={mode === ViewMode.READ}
						disabled={mode === ViewMode.READ || mode === ViewMode.EDIT}
						fieldErrors={fieldErrors}
						validateFields={validateFields}
						initialValues={initialValues}
						mode={mode}
						adminData={adminData}
						is2FAChecked={is2FAChecked}
						disable2FACheckbox={disable2FACheckbox}
						handleAdminDataChange={handleAdminDataChange}
						handleEnable2FA={handleEnable2FA}
					/>
				</div>
				<UserRolesGroup
					mode={mode}
					groupsList={groupsList}
					selectedGroups={selectedGroups}
					currentSelectedGroup={currentSelectedGroup}
					setCurrentSelectedGroup={setCurrentSelectedGroup}
					handleRemoveSelectedGroup={handleRemoveSelectedGroup}
					handleAddAnotherRole={handleAddAnotherRole}
				/>
				{hasMultipleLimitRoles && <p className="text-red-500 pt-2">{Constants?.MULTIPLE_APPROVAL_WARNING}</p>}
				<div className={styles?.group_wrapper}>
					{permissionList?.map((group: { title?: string; values?: Array<GroupPermissionItem> }, idx: number) => {
						const checkedRoles = group?.values?.reduce((roles: Array<string>, { perm_code }: { perm_code: string }) => {
							if (selectedRoles?.includes(perm_code)) {
								roles.push(perm_code);
							}
							return roles;
						}, []);
						return <RoleGroup readonly checkedRoles={checkedRoles} title={group?.title} values={group.values} key={`${group.title}-${idx}`} />;
					})}
				</div>
				<div className={styles?.view_edit_buttons_wrapper}>
					<div className={styles?.buttons_wrapper}>
						{mode === ViewMode?.READ && isUserActive && (
							<>
								<Button type="primary" danger className={styles?.button} onClick={() => handleDeactivateUser()}>
									<span className={styles?.button_label}>{Constants.DEACTIVATE_BUTTON_LABEL}</span>
								</Button>
								{canResendPassword && (
									<Button className={styles?.reactivate_button} onClick={() => handleResendPassword()}>
										<span className={styles?.button_label}>{Constants.RESEND_PASSWORD_BUTTON_LABEL}</span>
									</Button>
								)}
								{/* Currently not in scope */}
								<Button danger className={`${styles?.button} hidden`} onClick={() => handleLockUser()}>
									<span className={styles?.lock_button_label}>{Constants.LOCK_ACCOUNT_LABEL}</span>
								</Button>
							</>
						)}
						{mode === ViewMode?.READ && !isUserActive && (
							<>
								<Button className={styles?.reactivate_button} onClick={() => activateUser()}>
									<span className={styles?.button_label}>{Constants.REACTIVATE_USER_LABEL}</span>
								</Button>
							</>
						)}
						{mode === ViewMode.EDIT && (
							<ButtonComponent
								onClick={submitChanges}
								type={isDetailsUpdated ? "active" : "disabled"}
								label={<span className={styles?.button_label}>{Constants.MAKE_CHANGES}</span>}
							/>
						)}
					</div>
					<div className={styles?.link_button_wrapper}>
						{mode === ViewMode.READ && isUserActive && createUserAccess && (
							<Button className={styles?.edit_button} type="link" onClick={() => handleMode(ViewMode.EDIT)}>
								<div className="flex flex-row items-center">{Constants.EDIT_ACCOUNT_LABEL}</div>
								<div className="flex items-center justify-center ml-[5px]">{getIcon({ icon: "editLink" })}</div>
							</Button>
						)}
						{mode === ViewMode.EDIT && (
							<Button className={styles?.edit_button} type="link" onClick={() => handleGoBack()}>
								<div className="flex flex-row items-center">{Constants.GO_BACK_LABEL}</div>
								<div className="flex items-center justify-center ml-[5px]">{getIcon({ icon: "editLink" })}</div>
							</Button>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default ViewEditUserPage;
